/**
 * Guards against count_items duplication — the pure logic behind the fix for
 * the template-seed runaway.
 *
 * What happened in production: seedEmptyExistingTemplates decided whether a
 * template session was "empty" by fetching raw count_items rows across ALL of
 * a store's template sessions in one query. PostgREST silently caps that read
 * at 1,000 rows. Once the store's template sessions collectively held more,
 * whichever templates' rows fell past the cap were judged empty and re-seeded
 * with their full item list — on every visit to the Counts screen, growing the
 * table, pushing more templates past the cap, forever. One store accumulated
 * 1.97 million junk rows; a 8-item count showed "1000 items" (the cap) split
 * across rooms.
 *
 * Three lessons, encoded here as reusable pieces:
 *
 *   - Emptiness must be decided per session with a COUNT, never by scanning
 *     capped rows (`isSessionEmpty` below wraps the head-count query).
 *   - A seeding routine must be idempotent across double-mounts and repeat
 *     loads (SeedRegistry), and must re-check emptiness immediately before
 *     inserting (the create-time race produced triple seeding within 23s).
 *   - Any copy of a session's items must read ALL rows (fetchAllCountItems
 *     paginates past the cap) and collapse duplicates (dedupeCountItemRows),
 *     so counting again from a historically-exploded session self-heals
 *     instead of propagating the junk.
 */

import { parNormalizationFrom } from '../utils/countUnitModel';

/** Page size for paginated reads. PostgREST's default hard cap is 1,000. */
const PAGE = 1000;

/**
 * Every count_items row for a session, however many there are.
 *
 * `.range()` pages explicitly so the read is immune to the server row cap —
 * the silent 1,000-row truncation is exactly how the runaway stayed invisible:
 * the app always *showed* a plausible list while the table exploded behind it.
 *
 * @param {object} client   supabase client
 * @param {number|string} sessionId
 * @param {string} [columns='*']
 * @returns {Promise<{ rows: Array, error: object|null }>}
 */
export async function fetchAllCountItems(client, sessionId, columns = '*') {
  const rows = [];
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await client
      .from('count_items')
      .select(columns)
      .eq('count_session_id', sessionId)
      .order('count_position', { ascending: true })
      .order('id', { ascending: true })
      .range(from, from + PAGE - 1);
    if (error) return { rows, error };
    rows.push(...(data || []));
    if (!data || data.length < PAGE) return { rows, error: null };
  }
}

/**
 * Whether a session has any count_items — decided with an exact head count,
 * which the row cap cannot lie to. Errors report as NOT empty: "we could not
 * check" must never become "so insert everything again".
 *
 * @returns {Promise<{ empty: boolean, count: number|null, error: object|null }>}
 */
export async function isSessionEmpty(client, sessionId) {
  const { count, error } = await client
    .from('count_items')
    .select('id', { count: 'exact', head: true })
    .eq('count_session_id', sessionId);
  if (error) return { empty: false, count: null, error };
  return { empty: (count ?? 0) === 0, count: count ?? 0, error: null };
}

/**
 * Which template sessions are genuinely empty, checked one head-count each.
 *
 * Replaces the capped raw-row scan. N is the number of template sessions a
 * store shows on one screen — single digits — so N head-only requests are
 * cheaper than the one row-scan they replace.
 *
 * @param {object} client
 * @param {Array<{id:*}>} templates
 * @returns {Promise<Array>} the subset of `templates` that are truly empty
 */
export async function findEmptyTemplateSessions(client, templates) {
  const list = (templates || []).filter((t) => t && t.id != null);
  if (list.length === 0) return [];
  const checks = await Promise.all(list.map((t) => isSessionEmpty(client, t.id)));
  return list.filter((_, i) => checks[i].empty);
}

/**
 * The identity half of a row's dedupe key. With a working-set groups
 * structure (utils/relatedItemGroups buildWorkingSetGroups) the row answers
 * to its GROUP — the count-through's "a related group is one countable
 * thing" — so a sibling member's row at the same location is a duplicate of
 * this one, not a second thing to count. Without groups, exactly the old
 * per-item identity.
 */
const rowIdentity = (row, groups) => {
  const itemId = row?.inventory_item_id ?? '';
  if (!groups || itemId === '' || itemId === null || itemId === undefined) return String(itemId ?? '');
  return groups.groupIdByItemId?.get?.(String(itemId)) || String(itemId);
};

/**
 * Collapses duplicate rows in a session item list.
 *
 * One row per (identity, location); the keeper is the earliest — lowest id
 * wins, because the original row predates every clone. Rows a human actually
 * touched (is_counted, notes, or a real quantity) always outrank untouched
 * clones, so deduping a mixed list can never discard a count.
 *
 * Identity is the item, or — when `opts.groups` carries the store's
 * working-set groups — the item's GROUP, so a re-seeded session materializes
 * one row per related group per location instead of one per member.
 *
 * CAUTION for callers: group-keyed deduping DROPS sibling members' rows. Use
 * it when building or reconciling a row set (seeding, missing-row repair),
 * never on a live session's rows before the count-group fold has captured
 * hidden siblings' typed counts (utils/countGroupFold owns that).
 *
 * @param {Array} rows count_items rows
 * @param {object} [opts]
 * @param {object} [opts.groups] buildWorkingSetGroups output
 * @returns {Array} deduped rows, original order of first appearance
 */
export function dedupeCountItemRows(rows, opts = {}) {
  const groups = opts?.groups || null;
  const byKey = new Map();
  const order = [];
  for (const row of rows || []) {
    if (!row) continue;
    const key = `${rowIdentity(row, groups)}::${row.location ?? row.item_snapshot?.location ?? ''}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, row);
      order.push(key);
      continue;
    }
    if (preferRow(row, existing)) byKey.set(key, row);
  }
  return order.map((k) => byKey.get(k));
}

/**
 * Build untouched count_items rows for template pairs that are missing from
 * an existing draft.
 *
 * This is intentionally identity-based, not name-based. Accepting a related
 * product can archive the vendor-specific inventory row and add the surviving
 * canonical row to the recurring template. A draft created before that merge
 * still contains the archived id, so merely hiding archived rows would make
 * the new canonical item disappear from the count. The template is the
 * forward-looking definition; this helper adds its new (item, location) pairs
 * while leaving every existing draft row untouched for the liveness pass to
 * handle.
 *
 * With `opts.groups` (the store's working-set groups) a template pair is
 * "present" when the draft already holds ANY member of its related group at
 * that location — the count-through's one-card-per-group rule. Without the
 * fold's group identity here, every open would re-materialize sibling rows
 * the fold exists to hide.
 *
 * @param {number|string} sessionId target draft count session
 * @param {Array} currentRows current count_items rows for the draft
 * @param {Array} templateRows live source rows from the recurring template
 * @param {object} [opts]
 * @param {object} [opts.groups] buildWorkingSetGroups output
 * @returns {Array<object>} insert-ready count_items payloads
 */
export function buildMissingCountItemsFromTemplate(sessionId, currentRows, templateRows, opts = {}) {
  if (sessionId === null || sessionId === undefined) return [];
  const groups = opts?.groups || null;

  const pairKey = (row) => {
    const itemId = row?.inventory_item_id ?? row?.item_snapshot?.id ?? '';
    const identity = itemId === '' ? '' : (groups?.groupIdByItemId?.get?.(String(itemId)) || String(itemId));
    const location = row?.location ?? row?.item_snapshot?.location ?? '';
    return `${identity}::${location}`;
  };

  const existingPairs = new Set(dedupeCountItemRows(currentRows, { groups }).map(pairKey));
  const sourceRows = dedupeCountItemRows(templateRows, { groups });
  const maxPosition = (currentRows || []).reduce(
    (max, row) => Math.max(max, Number(row?.count_position) || 0),
    0,
  );

  return sourceRows
    .filter((row) => {
      if (row?.inventory_item_id === null || row?.inventory_item_id === undefined) return false;
      const key = pairKey(row);
      if (existingPairs.has(key)) return false;
      existingPairs.add(key);
      return true;
    })
    .map((row, index) => ({
      count_session_id: sessionId,
      inventory_item_id: row.inventory_item_id,
      item_snapshot: row.item_snapshot || {},
      expected_quantity: row.expected_quantity
        ?? (row.par_level !== null && row.par_level !== undefined ? String(row.par_level) : '0'),
      actual_quantity: '',
      variance_quantity: 0,
      count_notes: '',
      is_counted: false,
      counted_at: null,
      par_level: row.par_level ?? null,
      par_unit: row.par_unit || 'each',
      par_defaulted: row.par_defaulted ?? row.item_snapshot?.par_defaulted ?? false,
      // The vendor-neutral PAR rides with the legacy pair, or a repaired row
      // silently loses the package a PAR of "1 case" was resolved against.
      ...parNormalizationFrom(row),
      min_order_qty: row.min_order_qty ?? row.item_snapshot?.moq ?? null,
      min_order_unit: row.min_order_unit || row.item_snapshot?.moq_unit || 'each',
      location: row.location || row.item_snapshot?.location || null,
      count_position: row.count_position ?? (maxPosition + (index + 1) * 10),
    }));
}

/** True when `a` should be kept over `b` for the same (item, location). */
function preferRow(a, b) {
  const touchedA = rowWasTouched(a);
  const touchedB = rowWasTouched(b);
  if (touchedA !== touchedB) return touchedA;
  // Neither (or both) touched: the earliest row is the original.
  const idA = Number(a.id);
  const idB = Number(b.id);
  if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) return idA < idB;
  const tA = Date.parse(a.created_at || '');
  const tB = Date.parse(b.created_at || '');
  if (Number.isFinite(tA) && Number.isFinite(tB)) return tA < tB;
  return false;
}

/** A human left a mark on this row: a count, a note, or a quantity. */
function rowWasTouched(row) {
  if (!row) return false;
  if (row.is_counted === true) return true;
  if (row.counted_at) return true;
  if (typeof row.count_notes === 'string' && row.count_notes.trim() !== '') return true;
  const qty = row.actual_quantity;
  return qty !== null && qty !== undefined && qty !== '' && Number(qty) !== 0;
}

/**
 * Idempotency for fire-and-forget seeding.
 *
 * Module-level, deliberately: the Counts screen can mount twice (the mobile
 * shell double-mounts), and the seeder is called from more than one place.
 * `begin` claims a session id; a second claim while the first is running or
 * after it finished is refused. `fail` releases the claim so a transient
 * network error can be retried on the NEXT load — but never in the same tick.
 */
export class SeedRegistry {
  constructor() {
    this.inFlight = new Set();
    this.done = new Set();
  }

  /** @returns {boolean} true if the caller may seed this session now */
  begin(sessionId) {
    const key = String(sessionId);
    if (this.inFlight.has(key) || this.done.has(key)) return false;
    this.inFlight.add(key);
    return true;
  }

  finish(sessionId) {
    const key = String(sessionId);
    this.inFlight.delete(key);
    this.done.add(key);
  }

  fail(sessionId) {
    this.inFlight.delete(String(sessionId));
  }
}
